import { useEffect, useRef } from 'react';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import { useAppStore } from '../state/store';
import { getCanonicalTimestampsForAllCharts, getIndicatorValuesOnTimeline } from '../lib/chartTimeline';
import { useSyncedCrosshair } from '../hooks/useSyncedCrosshair';
import {
  chartPanPlugin,
  CHART_RIGHT_AXIS_SIZE,
  createXAxisConfig,
  findIndexForTimestamp,
  getChartCursorSyncConfig,
  xAxisSecondRowPlugin,
} from './PrimarySeriesChart';
import {
  getChartBehavior,
  getIndicatorDisplayInfo,
  getIndicatorDisplayName,
} from '../utils/indicatorDisplayRegistry';

const CHART_HEIGHT = 280;

function formatLegendDate(timestamps: number[], idx: number): string {
  const i = Math.max(0, Math.min(timestamps.length - 1, Math.round(idx)));
  const sec = timestamps[i];
  if (sec == null) return '';
  const d = new Date(sec * 1000);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
}

/** Min/max across all value series (indices 1..data.length-1) in the given x range. */
function yRangeFromValueSeries(
  data: uPlot.AlignedData,
  iMin: number,
  iMax: number
): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;
  for (let s = 1; s < data.length; s++) {
    const arr = data[s] as (number | null)[];
    for (let i = Math.max(0, iMin); i <= Math.min(arr.length - 1, iMax); i++) {
      const v = arr[i];
      if (v != null && Number.isFinite(v)) {
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }
  }
  if (min === Infinity) min = 0;
  if (max === -Infinity) max = 1;
  if (min === max) {
    min = min - 1;
    max = max + 1;
  } else {
    const pad = (max - min) * 0.05;
    min -= pad;
    max += pad;
  }
  return { min, max };
}

type Props = {
  indicatorId: string;
};

export default function IndicatorChart({ indicatorId }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<uPlot | null>(null);
  const timestampsRef = useRef<number[]>([]);

  const indicators = useAppStore((s) => s.indicators);
  const indicatorSeries = useAppStore((s) => s.indicatorSeries);
  const ohlc = useAppStore((s) => s.ohlc);
  const primaryInstrumentId = useAppStore((s) => s.primaryInstrumentId);
  const instruments = useAppStore((s) => s.instruments);
  const backtestEquityCurve = useAppStore((s) => s.backtestEquityCurve);
  const chartTimeSync = useAppStore((s) => s.chartTimeSync);
  const primaryChartFullWidth = useAppStore((s) => s.primaryChartFullWidth);

  const indicator = indicators.find((i) => i.id === indicatorId);
  const chartId = `indicator-${indicatorId}` as const;

  useSyncedCrosshair(
    chartId,
    chartRef,
    timestampsRef,
    (ts) => findIndexForTimestamp(timestampsRef.current, ts ?? 0) ?? undefined
  );

  useEffect(() => {
    if (!containerRef.current || !primaryInstrumentId || !ohlc || !indicator || !indicatorSeries[indicatorId])
      return;

    const raw = indicatorSeries[indicatorId];
    const instrumentIds = instruments.map((i) => i.id);
    const equityTs =
      backtestEquityCurve?.timestamps.map((ts) => {
        const s = typeof ts === 'string' ? ts.slice(0, 10) : '';
        return Math.floor(new Date(s + 'T00:00:00Z').getTime() / 1000);
      }) ?? [];
    const timestamps = getCanonicalTimestampsForAllCharts(
      ohlc,
      primaryInstrumentId,
      instrumentIds,
      equityTs.length > 0 ? equityTs : undefined
    );
    if (timestamps.length === 0) return;

    timestampsRef.current = timestamps;

    const primarySymbol =
      instruments.find((i) => i.id === primaryInstrumentId)?.symbol ?? 'Primary';
    const mainIndicatorLabel = getIndicatorDisplayName(indicator, primarySymbol);

    const overlayData: (number | null)[][] = [];
    const overlaySeries: uPlot.Series[] = [];
    const seriesMap = indicatorSeries ?? {};

    // Overlay indicators that use this chart's indicator as their series (e.g. Rolling Min on ROC)
    const overlayCandidates = indicators.filter(
      (ind) =>
        ind.instrumentId === indicatorId &&
        getChartBehavior(ind.type) === 'overlay' &&
        seriesMap[ind.id]
    );
    const seriesLabelForOverlays = indicator.name || mainIndicatorLabel;
    overlayCandidates.forEach((overlayInd) => {
      const overlayRaw = seriesMap[overlayInd.id];
      const displayInfo = getIndicatorDisplayInfo(overlayInd.type);
      const emits = displayInfo?.emits ?? { Value: 'Number' };
      const fieldKeys = Object.keys(emits);
      const backendFields =
        fieldKeys.length > 1
          ? fieldKeys.map((k) => k.toLowerCase())
          : [undefined];
      backendFields.forEach((field, fi) => {
        const overlayValues = getIndicatorValuesOnTimeline(
          overlayRaw,
          timestamps,
          field
        );
        overlayData.push(overlayValues);
        const overlayLabel =
          fieldKeys.length > 1
            ? `${getIndicatorDisplayName(overlayInd, seriesLabelForOverlays)} ${fieldKeys[fi]}`
            : getIndicatorDisplayName(overlayInd, seriesLabelForOverlays);
        overlaySeries.push({
          label: overlayLabel,
          scale: 'y',
          stroke: displayInfo?.colors?.[fieldKeys[fi]] ?? displayInfo?.color ?? '#4a6572',
          width: displayInfo?.lineWidth ?? 2,
          spanGaps: true,
          value: (_u, rawVal) =>
            rawVal != null && Number.isFinite(rawVal as number)
              ? (rawVal as number).toFixed(4)
              : '--',
        });
      });
    });

    const displayInfoMain = getIndicatorDisplayInfo(indicator.type);
    const emitsMain = displayInfoMain?.emits ?? { Value: 'Number' };
    const fieldKeysMain = Object.keys(emitsMain);
    const isMultiFieldMain = fieldKeysMain.length > 1;
    const mainData: (number | null)[][] = isMultiFieldMain
      ? fieldKeysMain.map((k) =>
          getIndicatorValuesOnTimeline(
            raw,
            timestamps,
            k.toLowerCase()
          )
        )
      : [getIndicatorValuesOnTimeline(raw, timestamps)];
    const mainSeries: uPlot.Series[] = isMultiFieldMain
      ? fieldKeysMain.map((k, fi) => ({
          label: `${mainIndicatorLabel} ${k}`,
          scale: 'y' as const,
          stroke: displayInfoMain?.colors?.[fieldKeysMain[fi]] ?? displayInfoMain?.color ?? '#4a6572',
          width: displayInfoMain?.lineWidth ?? 2,
          spanGaps: true,
          value: (_u: uPlot, rawVal: unknown) =>
            rawVal != null && Number.isFinite(rawVal as number)
              ? (rawVal as number).toFixed(4)
              : '--',
        }))
      : [
          {
            label: mainIndicatorLabel,
            scale: 'y' as const,
            stroke: displayInfoMain?.color ?? '#4a6572',
            width: displayInfoMain?.lineWidth ?? 2,
            value: (_u: uPlot, rawVal: unknown) =>
              rawVal != null && Number.isFinite(rawVal as number)
                ? (rawVal as number).toFixed(4)
                : '--',
          },
        ];

    const data: uPlot.AlignedData = [
      Array.from({ length: timestamps.length }, (_, i) => i),
      ...mainData,
      ...overlayData,
    ];

    const n = data[0].length - 1;
    const sync = useAppStore.getState().chartTimeSync;
    let xMin = 0;
    let xMax = Math.max(0, n);
    if (sync.xMin != null && sync.xMax != null) {
      xMin = Math.max(0, Math.min(sync.xMin, n));
      xMax = Math.max(0, Math.min(sync.xMax, n));
      if (xMin >= xMax) {
        xMin = 0;
        xMax = n;
      }
    }

    const fullW = useAppStore.getState().primaryChartFullWidth;
    const width = fullW != null ? fullW : Math.max(containerRef.current.clientWidth, 100);

    const opts: uPlot.Options = {
      width,
      height: CHART_HEIGHT,
      plugins: [
        chartPanPlugin(
          (min, max) => useAppStore.getState().setChartTimeRange(min, max),
          (u, newMin, newMax) => {
            const iMin = Math.max(0, Math.floor(newMin));
            const iMax = Math.min(
              (u.data[1] as (number | null)[]).length - 1,
              Math.ceil(newMax)
            );
            const { min, max } = yRangeFromValueSeries(u.data, iMin, iMax);
            u.setScale('y', { min, max });
          },
          getChartCursorSyncConfig(chartId, (_u, idx) => timestamps[idx] ?? null)
        ),
        xAxisSecondRowPlugin(timestamps),
      ],
      scales: {
        x: { min: xMin, max: xMax, distr: 2 },
        y: {
          auto: true,
          range: (u, _dataMin, _dataMax) => {
            const scaleMin = u.scales.x.min ?? 0;
            const scaleMax = u.scales.x.max ?? scaleMin + 1;
            const iMin = Math.max(0, Math.floor(scaleMin));
            const iMax = Math.min(
              (u.data[1] as (number | null)[]).length - 1,
              Math.ceil(scaleMax)
            );
            const { min, max } = yRangeFromValueSeries(u.data, iMin, iMax);
            return [min, max];
          },
        },
      },
      series: [
        {
          label: 'Date',
          value: (_u, _rawValue, _seriesIdx, idx) =>
            idx != null ? formatLegendDate(timestamps, idx) : '--',
        },
        ...mainSeries,
        ...overlaySeries,
      ],
      axes: [
        createXAxisConfig(timestamps),
        {
          side: 1,
          scale: 'y',
          size: CHART_RIGHT_AXIS_SIZE,
          values: (_u, vals) => vals.map((v) => (v != null ? v.toFixed(2) : '')),
        },
      ],
    };

    const chart = new uPlot(opts, data, containerRef.current);
    chartRef.current = chart;

    const { min: yMin, max: yMax } = yRangeFromValueSeries(data, xMin, xMax);
    chart.setScale('y', { min: yMin, max: yMax });

    const ro = new ResizeObserver(() => {
      if (!containerRef.current || !chartRef.current) return;
      const w =
        useAppStore.getState().primaryChartFullWidth ?? containerRef.current.clientWidth;
      chartRef.current.setSize({ width: w, height: CHART_HEIGHT });
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      chart.destroy();
      chartRef.current = null;
    };
  }, [
    indicatorId,
    indicator,
    indicators,
    indicatorSeries,
    ohlc,
    primaryInstrumentId,
    instruments,
    backtestEquityCurve,
  ]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || primaryChartFullWidth == null) return;
    chart.setSize({ width: primaryChartFullWidth, height: CHART_HEIGHT });
  }, [primaryChartFullWidth]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || chartTimeSync.xMin == null || chartTimeSync.xMax == null) return;
    const n = (chart.data[0] as number[]).length - 1;
    const xMin = Math.max(0, Math.min(chartTimeSync.xMin, n));
    const xMax = Math.max(0, Math.min(chartTimeSync.xMax, n));
    if (xMin >= xMax) return;
    if (chart.scales.x.min === xMin && chart.scales.x.max === xMax) return;
    chart.batch(() => {
      chart.setScale('x', { min: xMin, max: xMax });
      const { min: yMin, max: yMax } = yRangeFromValueSeries(
        chart.data,
        xMin,
        xMax
      );
      chart.setScale('y', { min: yMin, max: yMax });
    });
  }, [chartTimeSync.xMin, chartTimeSync.xMax]);

  if (!indicator || !indicatorSeries[indicatorId]) return null;

  const displayLabel = getIndicatorDisplayName(
    indicator,
    instruments.find((i) => i.id === indicator.instrumentId)?.symbol ?? '?'
  );

  return (
    <div className="w-full">
      <p className="text-sm text-ink/80 mb-2">{displayLabel}</p>
      <div
        ref={containerRef}
        className="w-full"
        style={{
          height: CHART_HEIGHT,
          ...(primaryChartFullWidth != null && { width: primaryChartFullWidth }),
        }}
      />
    </div>
  );
}
